import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { FilamentTestData } from '../../lib/filamentStressStrain';
import { useLocation } from 'react-router-dom';

interface StressStrainChartProps {
  datasets: FilamentTestData[];
}

// グラフの軸範囲（固定）
const AXIS_CONFIG = {
  strainMin: 0,
  strainMax: 15,  // 15に固定（15超は表示しない）
  stressMin: 0,
  stressMax: 150  // 150に固定
};

// フィラメントごとの色（最大10色）
const FILAMENT_COLORS = [
  '#10b981', // 緑
  '#f97316', // オレンジ
  '#3b82f6', // 青
  '#ef4444', // 赤
  '#8b5cf6', // 紫
  '#ec4899', // ピンク
  '#14b8a6', // ティール
  '#f59e0b', // アンバー
  '#06b6d4', // シアン
  '#6366f1'  // インディゴ
];

const TEXT_JA = {
  noData: '表示可能なデータがありません'
};

const TEXT_EN = {
  noData: 'No data available'
};

export const StressStrainChart: React.FC<StressStrainChartProps> = ({ datasets }) => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;

  if (datasets.length === 0) {
    return (
      <div className="metal-panel p-8 text-center" data-graph-lang={isEnglish ? 'en' : 'ja'}>
        <i className="ri-line-chart-line text-metal-light text-4xl mb-4"></i>
        <p className="text-metal-light">{TEXT.noData}</p>
      </div>
    );
  }

  // 全データセットを統合してグラフ用データを作成
  // 各フィラメントのデータポイントを strain 値でマージ
  // 🔧 15超のデータは除外
  const mergedData: { [strain: string]: any } = {};

  datasets.forEach((dataset, index) => {
    dataset.data.forEach(point => {
      // 🔧 strain > 15 のデータは無視
      if (point.strain > AXIS_CONFIG.strainMax) {
        return;
      }

      const strainKey = point.strain.toFixed(3);
      
      if (!mergedData[strainKey]) {
        mergedData[strainKey] = { strain: point.strain };
      }
      
      mergedData[strainKey][dataset.filamentId] = point.stress;
    });
  });

  // オブジェクトを配列に変換してソート
  const chartData = Object.values(mergedData).sort((a, b) => a.strain - b.strain);

  // カスタムツールチップ
  const CustomTooltip = ({ active, label }: any) => {
    if (active && label != null) {
      const hoveredStrain = Number(label);
      
      // 各データセットから、ホバー位置(hoveredStrain)に最も近いデータポイントを探す
      const nearestPoints = datasets.map((dataset, i) => {
        let nearest: any = null;
        let minDiff = Infinity;
        
        for (const pt of dataset.data) {
          // strainMaxを超えたデータは元々グラフ表示から弾かれているため考慮しない
          if (pt.strain > AXIS_CONFIG.strainMax) continue;
          
          const diff = Math.abs(pt.strain - hoveredStrain);
          if (diff < minDiff) {
            minDiff = diff;
            nearest = pt;
          }
        }
        
        return {
          name: dataset.filamentName,
          color: FILAMENT_COLORS[i % FILAMENT_COLORS.length],
          stress: nearest ? nearest.stress : 0,
          diff: minDiff
        };
      }).filter(p => p.diff < 0.5); // 最も近い点が 0.5% 以上離れている場合は表示から除外

      if (nearestPoints.length === 0) return null;

      return (
        <div className="bg-graphite border border-metal p-3 rounded shadow-lg">
          <p className="text-white font-semibold mb-2">
            Strain: ~{hoveredStrain.toFixed(2)}%
          </p>
          {nearestPoints.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.stress.toFixed(2)} MPa
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-[500px]" data-graph-lang={isEnglish ? 'en' : 'ja'}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#4a4d52" />
          <XAxis
            dataKey="strain"
            type="number"
            domain={[AXIS_CONFIG.strainMin, AXIS_CONFIG.strainMax]}
            label={{ value: 'Strain (%)', position: 'insideBottom', offset: -5, fill: '#9ca3af' }}
            stroke="#9ca3af"
            tick={{ fill: '#9ca3af' }}
          />
          <YAxis
            type="number"
            domain={[AXIS_CONFIG.stressMin, AXIS_CONFIG.stressMax]}
            allowDataOverflow={true}
            padding={{ top: 0, bottom: 0 }}
            label={{ value: 'Stress (MPa)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
            stroke="#9ca3af"
            tick={{ fill: '#9ca3af' }}
            tickFormatter={(value) => {
              // 負値を表示しない（0未満は0として表示）
              return value < 0 ? '0' : value.toString();
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ color: '#fff' }}
            iconType="line"
          />
          {datasets.map((dataset, index) => (
            <Line
              key={dataset.filamentId}
              type="monotone"
              dataKey={dataset.filamentId}
              name={dataset.filamentName}
              stroke={FILAMENT_COLORS[index % FILAMENT_COLORS.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
